<template>
  <div class="w-full">
    <h2 class="text-2xl text-primary leading-10 font-inter font-bold tracking-tight mb-6">Bio</h2>
    <div class="flex flex-col gap-6">
      <div class="w-full">
        <label :class="labelClass" for="fullName">Full name</label>
        <DsInput
          id="fullName"
          name="fullName"
          v-model="payload.fullName"
          placeholder="Enter full name"
          :error="fieldError('fullName', ERROR_PATTERN_VALID_NAME)"
        />
      </div>

      <div class="w-full">
        <label :class="labelClass" for="familyName">Family name</label>
        <DsInput
          id="familyName"
          name="familyName"
          v-model="payload.familyName"
          placeholder="Enter family name"
          :error="fieldError('familyName', ERROR_PATTERN_VALID_NAME)"
        />
      </div>

      <div class="w-full">
        <label :class="labelClass" for="preferredName">Preferred name</label>
        <DsInput
          id="preferredName"
          name="preferredName"
          v-model="payload.preferredName"
          placeholder="Enter preferred name"
          :error="fieldError('preferredName', ERROR_PATTERN_VALID_NAME)"
        />
      </div>

      <div class="w-full">
        <label :class="labelClass" for="stageOfLife">Stage of life</label>
        <DsDropdown
          id="stageOfLife"
          name="stageOfLife"
          v-model="stageOfLifeValue"
          :options="stageOfLifeOptions"
          placeholder="Select an option"
        />
      </div>

      <div v-if="payload.stageOfLife === 'OTHERS'" class="w-full">
        <DsTextarea
          id="otherPhaseName"
          name="otherPhaseName"
          v-model="otherPhaseNameValue"
          placeholder="Please specify your stage of life"
          :error="otherPhaseNameError"
        />
      </div>

      <template v-if="payload.stageOfLife === 'STUDYING'">
        <div class="w-full">
          <label :class="labelClass" for="levelId">Level of study</label>
          <DsDropdown
            id="levelId"
            name="levelId"
            v-model="levelIdValue"
            :options="levelOfStudyOptions"
            placeholder="Select an option"
          />
        </div>

        <div v-if="payload.levelId" class="w-full">
          <label :class="labelClass" for="schoolId">School</label>
          <DsSearchableDropdown
            id="schoolId"
            name="schoolId"
            v-model="schoolIdValue"
            :options="schoolOptions"
            placeholder="Select an option"
            search-placeholder="Search school"
          />
        </div>

        <div v-if="isOtherSchool" class="w-full">
          <DsTextarea
            id="otherSchool"
            name="otherSchool"
            v-model="otherSchoolValue"
            placeholder="Enter school name here"
            :error="otherSchoolError"
          />
        </div>
      </template>

      <CareerFields
        v-if="careerFieldsEnabled"
        v-model="careerFields"
        :stage-of-life="payload.stageOfLife"
        :level-id="payload.levelId"
      />

      <div class="w-full">
        <span :class="labelClass">Gender</span>
        <div class="w-full flex gap-2">
          <!--
            The muted-border override is this app's `grey-100` colliding with
            the design system's: the DS draws a disabled card and its ring in
            grey-100 (#C3C2C1, a hairline), while this app's scale has
            grey-100 as its near-black ink — so a locked card came out ringed
            in black. grey-20 is the app's hairline.
          -->
          <DsRadioCard
            v-for="item in gender"
            :key="item.value"
            class="flex-1"
            :class="isGenderDisabled ? '!border-grey-20 [&_span]:!border-grey-20' : ''"
            name="gender"
            :label="`${item.label} ${item.icon}`"
            :value="item.value"
            :model-value="payload.gender"
            :disabled="isGenderDisabled"
            @update:modelValue="onGender"
          />
        </div>
      </div>

      <div class="w-full">
        <label :class="labelClass" for="nationality">Nationality</label>
        <DsSearchableDropdown
          id="nationality"
          name="nationality"
          v-model="nationalityValue"
          :options="nationalityOptions"
          placeholder="Enter nationality"
          search-placeholder="Search nationality"
        />
      </div>

      <!-- Giving per login: this login has not confirmed the member's personal id, so
           the API hides it and will not take a new one. Shown locked, not empty. -->
      <div
        v-if="payload.personalIdLocked"
        class="w-full flex flex-col gap-4"
        data-test="personal-id-locked"
      >
        <div class="w-full">
          <label :class="labelClass" for="dateOfBirthLocked">Date of Birth</label>
          <DsDatePicker
            id="dateOfBirthLocked"
            name="dateOfBirthLocked"
            :model-value="dateOfBirthValue"
            display-format="DD-MM-YYYY"
            disabled
          />
        </div>
        <div class="w-full">
          <label :class="labelClass" for="partialNricLocked">
            Last 4 chars of NRIC/FIN/Passport no.
          </label>
          <DsInput
            id="partialNricLocked"
            name="partialNricLocked"
            model-value=""
            placeholder="Hidden on this account"
            disabled
          />
        </div>
        <p class="text-sm font-inter font-medium text-secondary">
          To see or change these on this account, verify your personal ID in MyGiving first.
        </p>
        <DsLink size="sm" underline="always" class="self-start" @click="goToMyGiving">
          Verify in MyGiving
        </DsLink>
      </div>

      <!-- Show editable Date of Birth and NRIC fields when personalId doesn't exist -->
      <div v-else-if="!payload.personalId" class="w-full flex flex-col gap-4">
        <div class="w-full">
          <label :class="labelClass" for="dateOfBirth">Date of Birth</label>
          <DsDatePicker
            id="dateOfBirth"
            name="dateOfBirth"
            v-model="dateOfBirthValue"
            display-format="DD-MM-YYYY"
            placeholder="Enter your Date of Birth"
            :max="maxDate"
            :error="dateOfBirthError"
          />
        </div>
        <div class="w-full">
          <label :class="labelClass" for="partialNric">
            Last 4 chars of NRIC/FIN/Passport no.
          </label>
          <DsInput
            id="partialNric"
            name="partialNric"
            v-model="partialNricValue"
            placeholder="Eg: 123A"
            :max-length="4"
            :error="partialNricError"
          />
        </div>
        <DsLink size="sm" underline="always" class="self-start" @click="onInfo">
          What is this information used for?
        </DsLink>
      </div>

      <!-- Show BioRequestCard only when personalId exists -->
      <div v-if="payload.personalId && !payload.personalIdLocked">
        <BioRequestCard
          @hideFooter="hideFooter"
          @profile-refreshed="$emit('profileRefreshed')"
          :payload="payload"
          :showError="showError"
          :scrollElementId="scrollElementId"
        />
      </div>
    </div>
  </div>
  <div class="w-full pt-[40px]">
    <h2 class="text-2xl text-primary leading-10 font-inter font-bold tracking-tight mb-6">
      Contact Details
    </h2>
    <div class="flex flex-col gap-6">
      <!--
        Still the in-house field: the design system's Input and Verification
        have no slot for the inline country-code picker this one renders
        (DS_GAPS.md, section A).
      -->
      <CustomTextField
        label="Mobile number"
        placeholder="Enter mobile number"
        isPhone
        isVerify
        :verifyClick="onVerify"
        :errorMsg="ERROR_VALID_MOBILE"
        v-model:modelValue="payload.mobileNumber"
        v-model:countryValue="payload.countryCode"
        @hideFooter="hideFooter"
        :showError="showError.mobileNumber"
        :hideVerifyWhenUnchanged="isMobileNumberUnchanged"
      />
      <div class="w-full">
        <label :class="labelClass" for="email">Email</label>
        <DsVerification
          id="email"
          name="email"
          v-model="payload.email"
          placeholder="example@email.com"
          :verified="isEmailVerifiedAndUnchanged"
          :state="isEmailVerifiedAndUnchanged ? 'inactive-verified' : 'inactive'"
          :error="fieldError('email', ERROR_VALID_EMAIL)"
          :help-text="
            fieldError('email', ERROR_VALID_EMAIL)
              ? ''
              : 'You can use this email to login next time'
          "
          @verify="onVerifyEmail"
        />
      </div>
    </div>
  </div>
  <ModalSheet v-model="usedInfo" :removeSpace="true">
    <h4 class="text-2xl text-grey-700 mb-8 font-primary font-black tracking-[-0.03em]">
      What is this information used for?
    </h4>
    <p class="text-base font-medium font-inter text-brown-800 mb-4">
      We use your Partial NRIC with your Date of Birth to create a Personal ID.
    </p>
    <p class="text-base font-medium font-inter text-brown-800 mb-4">
      The Personal ID helps us accurately process your tithes, offerings and other forms of giving.
      This ensures your contributions are recorded and attributed to your account.
    </p>
    <p class="text-base font-medium font-inter text-brown-800 pb-[50%]">
      Rest assured, your information is handed securely and in accordance with our
      <a href="https://hogc.sg/privacy" target="_blank" class="underline">Privacy Policy</a>.
    </p>
  </ModalSheet>
</template>
<script>
import CustomTextField from '@/components/design/CustomTextField.vue';
import ModalSheet from '@/components/common/ModalSheet.vue';
import {
  DatePicker as DsDatePicker,
  Dropdown as DsDropdown,
  Input as DsInput,
  Link as DsLink,
  RadioCard as DsRadioCard,
  SearchableDropdown as DsSearchableDropdown,
  Textarea as DsTextarea,
  Verification as DsVerification,
} from '@team-hogc/hogc-one-design-system';
import {
  REGEX_NAME,
  REGEX_MOBILE,
  REGEX_EMAIL,
  REGEX_NRIC,
  REGEX_DOB,
  REGEX_SCHOOL_NAME,
  REGEX_STAGE_OF_LIFE,
} from '@/constants/validation/regex-patterns';
import {
  ERROR_PATTERN_VALID_NAME,
  ERROR_VALID_MOBILE,
  ERROR_VALID_EMAIL,
  ERROR_VALID_NRIC,
  ERROR_VALID_SCHOOL_NAME,
  ERROR_VALID_STAGE_OF_LIFE,
} from '@/constants/validation/error-messages';
import { stageOfLife, STAGE_OF_LIFE_TO_PHASE_ID } from '@/constants/profile/stageOfLife';
import BioRequestCard from './BioRequestCard.vue';
import CareerFields from '@/components/common/CareerFields.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { CAREER_FIELD_KEYS, PROFILE_CAREER_FIELDS_FLAG } from '@/constants/profile/careerFields';

// "Others" in the school list is a real id (0), so it has to be carried as a
// value rather than treated as "nothing selected".
const OTHER_SCHOOL_ID = 0;

export default {
  emits: ['hideFooter', 'numberVerify', 'emailVerify', 'profileRefreshed'],
  props: {
    payload: {
      type: Object,
      default: {},
    },
    originalPayload: {
      type: Object,
      default: {},
    },
    showError: {
      type: Object,
      default: {},
    },
    scrollElementId: {
      type: String,
    },
    nationalityList: {
      type: Array,
      default: [],
    },
    nationalityLoaded: {
      type: Boolean,
      default: false,
    },
    schoolsLoaded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BioRequestCard,
    CareerFields,
    CustomTextField,
    DsDatePicker,
    DsDropdown,
    DsInput,
    DsLink,
    DsRadioCard,
    DsSearchableDropdown,
    DsTextarea,
    DsVerification,
    ModalSheet,
  },
  data() {
    return {
      gender: [
        { label: 'Male', icon: '🙋🏻‍♂', value: 'M' },
        { label: 'Female', icon: '🙋🏻‍♀️', value: 'F' },
      ],
      usedInfo: false,
      labelClass: 'text-base text-primary font-inter font-medium block mb-1 leading-5',
      ERROR_PATTERN_VALID_NAME,
      ERROR_VALID_MOBILE,
      ERROR_VALID_EMAIL,
      ERROR_VALID_NRIC,
    };
  },
  computed: {
    careerFieldsEnabled() {
      const { isEnabled } = useFeatureFlags();
      return isEnabled(PROFILE_CAREER_FIELDS_FLAG);
    },
    careerFields: {
      get() {
        return Object.fromEntries(CAREER_FIELD_KEYS.map(key => [key, this.payload[key] ?? null]));
      },
      set(value) {
        Object.assign(this.payload, value);
      },
    },
    // The design-system fields match option values by identity and every id on
    // the payload is a number, so each one is proxied through a string.
    stageOfLifeValue: {
      get() {
        return this.payload.stageOfLife || '';
      },
      set(value) {
        this.payload.stageOfLife = value || null;
      },
    },
    levelIdValue: {
      get() {
        return this.toOptionValue(this.payload.levelId);
      },
      set(value) {
        this.payload.levelId = value === '' ? null : Number(value);
      },
    },
    schoolIdValue: {
      get() {
        return this.toOptionValue(this.payload.schoolId);
      },
      set(value) {
        this.payload.schoolId = value === '' ? null : Number(value);
      },
    },
    nationalityValue: {
      get() {
        return this.toOptionValue(this.payload.nationalityId);
      },
      set(value) {
        if (value === '' || value === null || value === undefined) return;
        this.payload.nationalityId = Number(value);
      },
    },
    otherSchoolValue: {
      get() {
        return this.payload.otherSchool || '';
      },
      set(value) {
        this.payload.otherSchool = value?.trim() ? value : null;
      },
    },
    otherPhaseNameValue: {
      get() {
        return this.payload.otherPhaseName || '';
      },
      set(value) {
        this.payload.otherPhaseName = value?.trim() ? value : null;
      },
    },
    dateOfBirthValue: {
      get() {
        return this.payload.dateOfBirth || '';
      },
      set(value) {
        this.payload.dateOfBirth = value || null;
      },
    },
    partialNricValue: {
      get() {
        return this.payload.partialNric || '';
      },
      set(value) {
        this.payload.partialNric = value || '';
      },
    },
    isOtherSchool() {
      return Number(this.payload.schoolId) === OTHER_SCHOOL_ID && this.payload.schoolId !== null;
    },
    stageOfLifeOptions() {
      return stageOfLife.map(({ value, label }) => ({ value, label }));
    },
    levelOfStudyOptions() {
      const levels = this.$store.getters['profile/levelOfStudy'] || [];
      return levels.map(({ value, label }) => ({ value: String(value), label }));
    },
    schoolOptions() {
      const others = { value: String(OTHER_SCHOOL_ID), label: 'Others' };
      const levelId = this.payload.levelId;

      if (!levelId || !this.schoolsLoaded) return [others];

      const schools = this.$store.getters['profile/schoolsByLevel'](levelId);
      if (!schools || schools.length === 0) return [others];

      return [...schools.map(({ value, label }) => ({ value: String(value), label })), others];
    },
    nationalityOptions() {
      return (this.nationalityList || []).map(({ value, label }) => ({
        value: String(value),
        label,
      }));
    },
    otherSchoolError() {
      const value = (this.payload.otherSchool || '').trim();
      if (!value) return '';
      return REGEX_SCHOOL_NAME.test(value) ? '' : ERROR_VALID_SCHOOL_NAME;
    },
    otherPhaseNameError() {
      const value = (this.payload.otherPhaseName || '').trim();
      if (!value) return '';
      return REGEX_STAGE_OF_LIFE.test(value) ? '' : ERROR_VALID_STAGE_OF_LIFE;
    },
    dateOfBirthError() {
      return this.showError.dateOfBirth ? 'Enter a valid date of birth, not in the future' : '';
    },
    partialNricError() {
      return this.showError.partialNric ? ERROR_VALID_NRIC : '';
    },
    maxDate() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${now.getFullYear()}-${month}-${day}`;
    },
    isNricRequired() {
      return this.payload.hasBioUpdateRequest !== undefined;
    },
    isMobileNumberRequired() {
      return this.payload.isMobileNumberVerified !== 0;
    },
    isMobileNumberUnchanged() {
      const currentMobile = this.payload.mobileNumber || '';
      const originalMobile = this.originalPayload.mobileNumber || '';
      const currentCountryCode = this.payload.countryCode || '';
      const originalCountryCode = this.originalPayload.countryCode || '';

      // Normalize country codes (remove + if present for comparison)
      const normalizedCurrentCode = currentCountryCode.toString().replace(/^\+/, '');
      const normalizedOriginalCode = originalCountryCode.toString().replace(/^\+/, '');

      return currentMobile === originalMobile && normalizedCurrentCode === normalizedOriginalCode;
    },
    isEmailUnchanged() {
      const currentEmail = this.payload.email || '';
      const originalEmail = this.originalPayload.email || '';
      return currentEmail === originalEmail;
    },
    // The design system disables Verify and shows a "Verified" label instead of
    // hiding the button, which is what this card used to do for an untouched
    // address. It also needs a `state`: left at `default`, Verify is enabled only
    // while the field has focus, and the tap that blurs the field was lost.
    isEmailVerifiedAndUnchanged() {
      return this.isEmailUnchanged && Boolean(this.payload.isEmailVerified);
    },
    isGenderDisabled() {
      // Disable gender field if user already has a gender value (similar to onboarding/RegisterBio.vue)
      return !!(this.originalPayload.gender && this.originalPayload.gender.trim() !== '');
    },
  },
  methods: {
    toOptionValue(value) {
      return value === null || value === undefined || value === '' ? '' : String(value);
    },
    fieldError(field, message) {
      return this.showError[field] ? message : '';
    },
    onInfo() {
      this.usedInfo = !this.usedInfo;
    },
    goToMyGiving() {
      this.$router.push('/my-giving');
    },
    onGender(value) {
      if (this.isGenderDisabled) return;
      this.payload.gender = value;
    },
    isValidFullName(value) {
      if (!value) return false;
      return REGEX_NAME.test(value);
    },
    isValidName(value) {
      if (!value) return true;
      return REGEX_NAME.test(value);
    },
    isValidMobileNumber(value) {
      if (!this.isMobileNumberRequired && !value) return true;
      if (!value) return false; // Prevents errors on empty values
      return REGEX_MOBILE.test(value);
    },
    isValidEmail(value) {
      return REGEX_EMAIL.test(value);
    },
    isValidNRIC(value) {
      if (!this.isNricRequired && !value) return true;
      if (!value) return false;
      return REGEX_NRIC.test(value);
    },
    isValidDOB(value) {
      // Validate DD-MM-YYYY format using regex
      if (!REGEX_DOB.test(value)) {
        return false; // Format is incorrect
      }

      // Extract day, month, and year
      const [day, month, year] = value.split('-').map(Number);

      // Ensure correct date conversion (months in JS Date start from 0)
      const inputDate = new Date(year, month - 1, day);
      const today = new Date();

      // Remove time from today's date for accurate comparison
      today.setHours(0, 0, 0, 0);
      inputDate.setHours(0, 0, 0, 0);

      // Ensure input date is not in the future
      const isValid = inputDate <= today;

      return isValid;
    },
    formatDateToDDMMYYYY(value) {
      const date = new Date(value);
      const day = `0${date.getDate()}`.slice(-2);
      const month = `0${date.getMonth() + 1}`.slice(-2);
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    onVerify() {
      this.$emit('number-verify');
    },
    onVerifyEmail() {
      this.$emit('email-verify');
    },
    hideFooter(val) {
      this.$emit('hideFooter', val);
    },
  },
  watch: {
    'payload.stageOfLife': {
      handler(newVal) {
        this.payload.phaseId = STAGE_OF_LIFE_TO_PHASE_ID[newVal] || null;
        if (newVal !== 'STUDYING') {
          this.payload.schoolId = null;
          this.payload.schoolName = '';
          this.payload.levelId = null;
        }
        if (newVal !== 'OTHERS') {
          this.payload.otherPhaseName = null;
        }
      },
    },
    'payload.levelId': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && oldVal && this.payload.stageOfLife === 'STUDYING') {
          this.payload.schoolId = null;
          this.payload.schoolName = '';
        } else if (newVal && this.payload.schoolId) {
          const schools = this.$store.getters['profile/schoolsByLevel'](newVal) || [];
          const selectedSchool = schools.find(school => school.value === this.payload.schoolId);
          this.payload.schoolName = selectedSchool ? selectedSchool.label : '';
        }
      },
    },
    'payload.schoolId': {
      handler(newVal) {
        if (newVal === OTHER_SCHOOL_ID) {
          this.payload.schoolName = this.payload.otherSchool || '';
        } else if (newVal && this.payload.levelId) {
          const schools = this.$store.getters['profile/schoolsByLevel'](this.payload.levelId) || [];
          const selectedSchool = schools.find(school => school.value === newVal);
          this.payload.schoolName = selectedSchool ? selectedSchool.label : '';
        } else {
          this.payload.schoolName = '';
        }
      },
      immediate: true,
    },
    'payload.otherSchool': {
      handler(newVal) {
        if (Number(this.payload.schoolId) === OTHER_SCHOOL_ID) {
          this.payload.schoolName = newVal || '';
        }
      },
      immediate: true,
    },
    'payload.fullName'(newVal) {
      this.showError.fullName = !this.isValidFullName(newVal);
    },
    'payload.preferredName'(newVal) {
      this.showError.preferredName = !this.isValidName(newVal);
    },
    'payload.mobileNumber'(newVal) {
      this.showError.mobileNumber = !this.isValidMobileNumber(newVal);
    },
    'payload.email'(newVal) {
      this.showError.email = !this.isValidEmail(newVal);
    },
    'payload.dateOfBirth'(newVal) {
      this.showError.dateOfBirth = !this.isValidDOB(this.formatDateToDDMMYYYY(newVal));
    },
    'payload.partialNric'(newVal) {
      this.showError.partialNric = !this.isValidNRIC(newVal);
    },
    usedInfo(newVal) {
      if (newVal === true) {
        document.getElementById(this.scrollElementId).scrollY = false;
      } else {
        document.getElementById(this.scrollElementId).scrollY = true;
      }
      this.$emit('hideFooter', this.usedInfo);
    },
  },
};
</script>
